// Shared projects in the app (W9a, PROTOCOL.md §10): the project key as the relay has it now (after a rotation), the
// owner's Share Project… model (invites, pending acceptances with their safety codes, members), and key rotation —
// a new key, wraps for the owner and every remaining member, then every document re-uploaded as a snapshot under it.
// A rotation is recorded here before it starts (`rotation:` in the offline store, the new key wrapped under the AVK),
// so one interrupted part-way resumes at the project's next open.
import * as A from '@automerge/automerge';
import { ApiError } from '@writing-suite/client/net/api.ts';
import { parseAddress } from '@writing-suite/client/net/address.ts';
import * as S from '@writing-suite/client/net/shares.ts';
import { codeWith, confirmMember, LIMITS, openShare, type ShareRole } from '@writing-suite/client/net/sharing.ts';
import { SyncConnection } from '@writing-suite/client/net/sync.ts';
import { fromB64u, toB64u, toHex, utf8 } from '@writing-suite/client/shared/b64.ts';
import { fingerprint, random, seal, sealSummary, sha256, unwrapProjectKey, wrapProjectKey, type ProjectSummary } from '@writing-suite/client/shared/crypto.ts';
import { wrapForMember } from '@writing-suite/client/shared/members.ts';
import type { Account } from './account.ts';
import { fetchProjects } from './library.ts';
import type { LiveProject } from './project.ts';

export { LIMITS };
export type { ShareRole };

export const ROLE_NAMES: Record<ShareRole, string> = { cowriter: 'Co-writer', commenter: 'Commenter' };

// The key now ---------------------------------------------------------------------------------------------------------

/** The project's key as the relay has it now: the owner's AVK wrap, or a member's (their AVK copy, else the member
 *  wrap). Null when it can't be had (no longer a member, or offline). */
export async function fetchProjectKey(account: Account, address: string): Promise<Uint8Array | null> {
  const a = parseAddress(address);
  if (!a) return null;
  const api = account.session.api;
  if (!a.owner) {
    const p = (await fetchProjects(api)).find((x) => x.id === a.projectId);
    return p ? unwrapProjectKey(account.avk, account.accountId, p.id, fromB64u(p.wrappedKey)) : null;
  }
  const s = (await S.listShares(api)).find((x) => x.owner === a.owner && x.projectId === a.projectId);
  if (!s || s.status !== 'active') return null;
  return (await openShare(api, account.accountId, account.avk, () => account.session.exchangeKey().catch(() => null), s)).key;
}

// Verified safety codes (per browser) -----------------------------------------------------------------------------------

const VERIFIED = (accountId: string) => `ws:verifiedCodes-${accountId}`;

function readVerified(accountId: string): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(VERIFIED(accountId)) ?? '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

/** Whether this browser recorded the code as compared with that account (a changed key makes it unverified again). */
export const isVerified = (accountId: string, other: string, code: string) => readVerified(accountId)[other] === code;

/** Records a safety code as compared with another account. */
export function markVerified(accountId: string, other: string, code: string) {
  try {
    localStorage.setItem(VERIFIED(accountId), JSON.stringify({ ...readVerified(accountId), [other]: code }));
  } catch {
    // this page only
  }
}

// The owner's Share Project… ------------------------------------------------------------------------------------------

export interface MemberRow extends S.Member {
  /** The safety code of the owner's and the member's exchange keys (null without a key). */
  safetyCode: string | null;
  verified: boolean;
}

export interface Sharing {
  members: MemberRow[];
  invites: S.ShareInvite[];
  keyFingerprint: string;
  needsRotation: boolean;
  limits: { cowriters: number; commenters: number };
  /** Pending members, active members and live invites per role (what the limits count). */
  used: Record<ShareRole, number>;
}

const live = (i: S.ShareInvite, now: number) => !i.acceptedAt && !i.revokedAt && i.expiresAt > now;

/** Everything Share Project… shows, with each member's safety code. */
export async function loadSharing(account: Account, projectId: string, now = Date.now()): Promise<Sharing> {
  const api = account.session.api;
  const [info, invites, own] = await Promise.all([S.listMembers(api, projectId), S.listShareInvites(api, projectId), account.session.exchangeKey()]);
  const members = await Promise.all(
    info.members.map(async (m): Promise<MemberRow> => {
      const safetyCode = own && m.publicKey ? await codeWith(own, m.publicKey) : null;
      return { ...m, safetyCode, verified: !!safetyCode && isVerified(account.accountId, m.account, safetyCode) };
    }),
  );
  const used = { cowriter: 0, commenter: 0 } as Record<ShareRole, number>;
  for (const m of members) if (m.status !== 'removed') used[m.role]++;
  for (const i of invites) if (live(i, now)) used[i.role]++;
  return { members, invites, keyFingerprint: info.keyFingerprint, needsRotation: info.needsRotation, limits: info.limits, used };
}

/** What a refusal at a limit means, in the writer's words (`409 membersFull`), else null. */
export function limitMessage(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.code !== 'membersFull') return null;
  const role = (e.details.role as ShareRole) ?? 'cowriter';
  const limit = Number(e.details.limit ?? (role === 'cowriter' ? LIMITS.cowriter : LIMITS.commenter));
  const who = role === 'cowriter' ? `${limit} co-writers` : `${limit} commenters`;
  return `A project can have up to ${who} besides you, counting invitations not yet accepted. Withdraw an invitation or remove someone first.`;
}

/** Confirms a pending member once the safety code was compared: the key wrapped to them, the code remembered here. */
export async function confirm(account: Account, projectId: string, key: Uint8Array, m: MemberRow): Promise<void> {
  if (!m.publicKey) throw new Error(`${m.name ?? 'They'} have no key to share with yet.`);
  await confirmMember(account.session.api, account.accountId, projectId, { account: m.account, role: m.role, publicKey: m.publicKey }, key);
  if (m.safetyCode) markVerified(account.accountId, m.account, m.safetyCode);
}

// Rotation ------------------------------------------------------------------------------------------------------------

export interface RotationProgress {
  step: 'key' | 'wraps' | 'upload' | 'done';
  done: number;
  total: number;
}

/** What a rotation asks of the relay (injectable for tests; by default the API and a sync connection). */
export interface RotationIO {
  listMembers(projectId: string): Promise<S.MembersInfo>;
  rotateProject(projectId: string, body: { newFingerprint: string; ownerWrap: string; memberWraps: Record<string, string> }): Promise<void>;
  putSummary(projectId: string, sealed: string): Promise<void>;
  connection(projectId: string): Pick<SyncConnection, 'connect' | 'hello' | 'append' | 'close'>;
}

function relayIO(account: Account): RotationIO {
  const api = account.session.api;
  return {
    listMembers: (id) => S.listMembers(api, id),
    rotateProject: (id, body) => S.rotateProject(api, id, body),
    putSummary: async (id, summary) => void (await api.call('PUT', `/api/projects/${encodeURIComponent(id)}/summary`, { summary })),
    connection: (id) => new SyncConnection(api, id),
  };
}

const rotations = new Map<string, Promise<Uint8Array>>();

/** A deterministic cid per new key and document, so a resumed upload's re-sends are the relay's `dup`s. */
async function rotationCid(fp: string, doc: string) {
  return `r-${fp}-${toHex((await sha256(utf8(doc))).subarray(0, 16))}`;
}

/**
 * Rotates (or resumes rotating) the key of one of the account's own projects: a new key recorded here first, wraps for
 * the owner and every active member (`POST …/rotate`), the open project re-keyed (its kept changes re-sealed), every
 * document re-uploaded as a snapshot under the new key, and the summary re-sealed. Returns the new key. One at a time
 * per project.
 */
export function rotateKey(o: {
  account: Account;
  projectId: string;
  /** The key now (the project's, before the rotation). */
  key: Uint8Array;
  /** The project open live (its documents are uploaded, and it takes the new key). */
  live: Pick<LiveProject, 'rekey' | 'snapshotSource'>;
  io?: RotationIO;
  summary?: ProjectSummary | null;
  onProgress?: (p: RotationProgress) => void;
}): Promise<Uint8Array> {
  const running = rotations.get(o.projectId);
  if (running) return running;
  const run = doRotate(o).finally(() => rotations.delete(o.projectId));
  rotations.set(o.projectId, run);
  return run;
}

async function doRotate(o: Parameters<typeof rotateKey>[0]): Promise<Uint8Array> {
  const { account, projectId } = o;
  const io = o.io ?? relayIO(account);
  const store = account.store;
  const progress = (step: RotationProgress['step'], done = 0, total = 0) => o.onProgress?.({ step, done, total });
  progress('key');
  let pending = await store.rotation(projectId);
  if (!pending) {
    const fresh = random(32);
    pending = {
      wrappedKey: toB64u(await wrapProjectKey(account.avk, account.accountId, projectId, fresh)),
      fingerprint: await fingerprint(fresh),
      previousWrappedKey: toB64u(await wrapProjectKey(account.avk, account.accountId, projectId, o.key)),
      posted: false,
      uploaded: [],
      startedAt: Date.now(),
    };
    await store.putRotation(projectId, pending);
  }
  const key = await unwrapProjectKey(account.avk, account.accountId, projectId, fromB64u(pending.wrappedKey));

  if (!pending.posted) {
    progress('wraps');
    const info = await io.listMembers(projectId);
    if (info.keyFingerprint === pending.fingerprint) {
      pending.posted = true; // recorded before this page could say so
    } else {
      const post = async (members: S.Member[]) => {
        const memberWraps: Record<string, string> = {};
        for (const m of members.filter((x) => x.status === 'active')) {
          if (!m.publicKey) continue;
          memberWraps[m.account] = toB64u(await wrapForMember(fromB64u(m.publicKey), account.accountId, projectId, m.account, key));
        }
        await io.rotateProject(projectId, { newFingerprint: pending!.fingerprint, ownerWrap: pending!.wrappedKey, memberWraps });
      };
      try {
        await post(info.members);
      } catch (e) {
        // Someone joined or left meanwhile: once more with the list as it is now.
        if (!(e instanceof ApiError && (e.code === 'missingWraps' || e.code === 'invalid'))) throw e;
        await post((await io.listMembers(projectId)).members);
      }
      pending.posted = true;
    }
    await store.putRotation(projectId, pending);
  }

  // From here the relay takes only the new key: the open project re-seals what it keeps and sends it again.
  await o.live.rekey(key);

  const source = o.live.snapshotSource();
  const docs = source.docs;
  const total = docs.length;
  const done = new Set(pending.uploaded);
  progress('upload', done.size, total);
  const conn = io.connection(projectId);
  await conn.connect();
  try {
    const { limits } = await conn.hello(source.cursor);
    const fp = pending.fingerprint;
    let sinceSave = 0;
    for (const d of docs) {
      if (done.has(d.name)) continue;
      const bytes = A.save(d.doc);
      const heads = [...A.getHeads(d.doc)].map((h) => h.toLowerCase()).sort().join('');
      const r = await conn.append(
        'snapshot',
        { doc: d.name, epoch: d.epoch, lineage: d.lineage, cid: await rotationCid(fp, d.name), heads, base: source.cursor, keyFingerprint: fp },
        await seal(key, bytes),
        limits,
      );
      // A newer epoch on the relay than this copy (compacted elsewhere): its own snapshot stands; nothing to replace.
      if (r.t === 'error' && r.code !== 'staleEpoch') throw new Error(`${d.name}: ${r.message}`);
      done.add(d.name);
      progress('upload', done.size, total);
      if (++sinceSave >= 20) {
        sinceSave = 0;
        await store.putRotation(projectId, { ...pending, uploaded: [...done] });
      }
    }
  } finally {
    conn.close();
  }
  if (o.summary) {
    await io.putSummary(projectId, toB64u(await sealSummary(key, { ...o.summary, v: 1 }))).catch(() => {});
  }
  await store.putRotation(projectId, null);
  progress('done', total, total);
  return key;
}
